import React, { useState, useEffect, useCallback } from 'react';
import { membersAPI, hubError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { UserPlus, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

/** Search approved members of the church and add them to a ministry or group. */
export function MemberPicker({ excludeIds = [], onAdd, testIdPrefix }) {
  const { selectedChurchId } = useAuth();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const fetchMembers = useCallback((term) => {
    setLoading(true);
    membersAPI.list(selectedChurchId, 'approved', term || undefined)
      .then(res => setResults((res.data?.data || []).slice(0, 25)))
      .catch(err => toast.error(hubError(err, 'Error al buscar miembros')))
      .finally(() => setLoading(false));
  }, [selectedChurchId]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleAdd = async (member) => {
    setAddingId(member.id);
    try {
      await onAdd(member);
    } finally {
      setAddingId(null);
    }
  };

  const available = results.filter(m => !excludeIds.includes(m.id));

  return (
    <div className="border-t pt-4 space-y-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">Agregar integrante</p>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchMembers(search)}
          placeholder="Buscar miembro aprobado..."
          className="pl-9"
          data-testid={`${testIdPrefix}-member-search`}
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 spinner text-[#056A84]" /></div>
      ) : available.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3">No hay miembros disponibles para agregar</p>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid={`${testIdPrefix}-member-options`}>
          {available.map(m => (
            <div key={m.id} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-accent/40">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-[#1F9D7A]/10 flex items-center justify-center text-[11px] font-medium text-[#1F9D7A]">
                  {(m.firstname || 'U')[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-sm truncate">{m.firstname || ''} {m.lastname || ''}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{m.email}</p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="text-xs shrink-0"
                disabled={addingId === m.id}
                onClick={() => handleAdd(m)}
                data-testid={`${testIdPrefix}-member-add-${m.id}`}
              >
                {addingId === m.id ? <Loader2 className="w-3 h-3 spinner" /> : <UserPlus className="w-3 h-3" />}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MemberPicker;
